import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/logger";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LifeBuoy, Loader2, Send, Inbox, MessageSquare, CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "billing", label: "Billing" },
  { value: "technical", label: "Technical" },
  { value: "feature", label: "Feature Request" },
  { value: "password_reset", label: "Password Reset" },
  { value: "other", label: "Other" },
];

const CATEGORY_LABEL = CATEGORIES.reduce((m, c) => ({ ...m, [c.value]: c.label }), {});

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return iso;
  }
}

// One ticket row in "My Support Requests" with an expandable thread + follow-up.
function MyTicketRow({ ticket, onReplied }) {
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const res = await api.userReplySupportTicket(ticket.id, reply.trim());
      setReply("");
      toast.success("Reply sent.");
      onReplied?.(res.ticket);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send reply.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-lg border border-border" data-testid="my-support-ticket">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left"
      >
        <span className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
          <span className="font-medium text-foreground truncate">{ticket.subject}</span>
        </span>
        <span className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{CATEGORY_LABEL[ticket.category] || "Other"}</span>
          {ticket.has_admin_reply && (
            <span className="inline-flex items-center gap-1 text-[11px] text-green-600 font-medium">
              <CheckCircle2 className="w-3.5 h-3.5" /> Replied
            </span>
          )}
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2.5 border-t border-border/60 pt-2.5">
          {(ticket.thread || []).map((e) => (
            <div
              key={e.id}
              className={`text-sm rounded-md px-3 py-2 ${
                e.author === "admin"
                  ? "bg-primary/5 border border-primary/20"
                  : "bg-muted/50 border border-border"
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-xs font-medium text-foreground">
                  {e.author === "admin" ? "SalesTap Support" : "You"}
                </span>
                <span className="text-[10px] text-muted-foreground">{fmtDate(e.created_at)}</span>
              </div>
              <p className="text-foreground/90 whitespace-pre-wrap break-words">{e.body}</p>
            </div>
          ))}
          {/* Follow-up */}
          <div className="flex items-end gap-2 pt-1">
            <Textarea
              data-testid="my-support-reply-input"
              value={reply}
              onChange={(ev) => setReply(ev.target.value)}
              placeholder="Add a follow-up…"
              rows={2}
              className="flex-1 text-sm"
            />
            <Button
              data-testid="my-support-reply-send"
              onClick={send}
              disabled={sending || !reply.trim()}
              size="sm"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Contact Support dialog.
 * - mode="authenticated": tabs "New Request" + "My Requests". Name/email come
 *   from the logged-in account (not shown as inputs).
 * - mode="public": single "New Request" form; user enters name + email (used
 *   when not logged in / can't sign in / forgot password).
 */
export default function SupportDialog({
  open,
  onOpenChange,
  mode = "authenticated",
  user,
  presetCategory,
  presetSubject,
  presetMessage,
}) {
  const isPublic = mode === "public";
  const [view, setView] = useState("new"); // "new" | "mine"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("technical");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [loadingMine, setLoadingMine] = useState(false);

  const resetForm = useCallback(() => {
    setSubject("");
    setCategory("technical");
    setMessage("");
    if (isPublic) {
      setName("");
      setEmail("");
    }
  }, [isPublic]);

  // Reset to the "new" view each time the dialog opens, applying any presets
  // (e.g. the "Forgot password?" flow opens this pre-set to Password Reset).
  useEffect(() => {
    if (open) {
      setView("new");
      if (presetCategory) setCategory(presetCategory);
      if (presetSubject !== undefined) setSubject(presetSubject || "");
      if (presetMessage !== undefined) setMessage(presetMessage || "");
    }
  }, [open, presetCategory, presetSubject, presetMessage]);

  const loadMine = useCallback(async () => {
    setLoadingMine(true);
    try {
      const res = await api.mySupportTickets();
      setTickets(res.tickets || []);
    } catch (e) {
      logError("Failed to load support tickets:", e);
    } finally {
      setLoadingMine(false);
    }
  }, []);

  useEffect(() => {
    if (open && !isPublic && view === "mine") loadMine();
  }, [open, isPublic, view, loadMine]);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim()) {
      toast.error("Please enter a subject.");
      return;
    }
    if (!message.trim()) {
      toast.error("Please enter a message.");
      return;
    }
    if (isPublic && (!name.trim() || !email.trim())) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      if (isPublic) {
        await api.createPublicSupportTicket({
          name: name.trim(),
          email: email.trim(),
          subject: subject.trim(),
          category,
          message: message.trim(),
        });
      } else {
        await api.createSupportTicket({
          subject: subject.trim(),
          category,
          message: message.trim(),
        });
      }
      // Neutral confirmation for public password-reset requests so we don't
      // reveal whether an email is registered.
      if (isPublic && category === "password_reset") {
        toast.success("If an account exists for that email, our team will help you reset it.");
      } else {
        toast.success("Your message was sent to SalesTap support.");
      }
      resetForm();
      if (isPublic) {
        onOpenChange(false);
      } else {
        setView("mine");
        loadMine();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your message.");
    } finally {
      setSubmitting(false);
    }
  };

  const NewRequestForm = (
    <form onSubmit={submit} className="space-y-4 mt-1" data-testid="support-form">
      {isPublic && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="support-name">Your name</Label>
            <Input
              id="support-name"
              data-testid="support-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="support-email">Your email</Label>
            <Input
              id="support-email"
              data-testid="support-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
          </div>
        </div>
      )}
      {!isPublic && user?.email && (
        <p className="text-xs text-muted-foreground">
          Sending as <span className="font-medium text-foreground">{user.email}</span>
        </p>
      )}
      <div className="grid sm:grid-cols-[1fr_180px] gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="support-subject">Subject</Label>
          <Input
            id="support-subject"
            data-testid="support-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Briefly, how can we help?"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="support-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="support-message">Message</Label>
        <Textarea
          id="support-message"
          data-testid="support-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Tell us what's going on…"
          rows={5}
        />
      </div>
      <Button type="submit" data-testid="support-submit" disabled={submitting} className="w-full">
        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
        Send message
      </Button>
    </form>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="support-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="w-5 h-5 text-primary" />
            Contact SalesTap Support
          </DialogTitle>
          <DialogDescription>
            {isPublic
              ? "Having trouble signing in or have a question? Send us a message and we'll get back to you."
              : "Reach our team for any reason. We'll reply right here in your requests."}
          </DialogDescription>
        </DialogHeader>

        {isPublic ? (
          NewRequestForm
        ) : (
          <>
            {/* Tabs */}
            <div className="inline-flex rounded-md border border-border overflow-hidden self-start" data-testid="support-tabs">
              <button
                type="button"
                data-testid="support-tab-new"
                onClick={() => setView("new")}
                className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                  view === "new" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                }`}
              >
                <MessageSquare className="w-3.5 h-3.5" /> New Request
              </button>
              <button
                type="button"
                data-testid="support-tab-mine"
                onClick={() => setView("mine")}
                className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                  view === "mine" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                }`}
              >
                <Inbox className="w-3.5 h-3.5" /> My Requests
              </button>
            </div>

            {view === "new" ? (
              NewRequestForm
            ) : (
              <div className="mt-1" data-testid="my-support-list">
                {loadingMine ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : tickets.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-10">
                    You haven't sent any support requests yet.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                    {tickets.map((t) => (
                      <MyTicketRow
                        key={t.id}
                        ticket={t}
                        onReplied={(updated) =>
                          setTickets((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
